<template>
  <div class="match-list-standard">
    <!-- 标题行 -->
    <div class="header">
      <div class="header-item header-time-league">赛事</div>
      <div class="header-item header-progress">时间</div>
      <div class="header-item header-status">状态</div>
      <div class="header-item teams-single teams-single-home">主队</div>
      <div class="header-item teams-single teams-single-away">客队</div>
      <div class="header-item half-score">半场</div>
      <div class="header-item corner">角球</div>
      <div 
        class="header-item betting-info odds-filter-trigger" 
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
      >
        即时指数
        <svg class="filter-icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 6L8 11L13 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        
        <!-- 指数筛选下拉菜单 -->
        <OddsFilterDropdown
          :visible="showOddsDropdown"
          :initial-selected="selectedOddsTypes"
          @update:visible="showOddsDropdown = $event"
          @odds-filter-change="handleOddsFilterChange"
          @mouseenter="handleMouseEnter"
          @mouseleave="handleMouseLeave"
        />
      </div>
      <div class="header-item icons">直播</div>
      <div class="header-item favorite">数据</div>
      <div class="header-item favorite">操作</div>
    </div>
    <!-- 比赛列表 -->
    <div class="list-container">
      <div class="list">
        <!-- 渲染所有分组和比赛 -->
        <template v-for="group in groupedMatches" :key="`group-${group.status}`">
          <!-- 分组标题 -->
          <div v-if="group.matches.length > 0" class="group-header">
            {{ getGroupTitle(group.status) }}
          </div>

          <!-- 比赛项目 -->
          <div
            v-for="match in group.matches"
            :key="`match-${match.match_id}`"
            class="match-item"
            :class="isSelected(match) && 'active'"
            @click="() => selectMatch(match)"
          >
            <div class="time-league" :class="getHotClass(match.hot_desc)">
              <div class="league-container">
                <div class="league">{{ match.title }}</div>
              </div>
            </div>
            <div class="time">{{ formatTime(match.matchTime) }}</div>
            <div class="progress" :class="{ 'progress-red': match.status === 2 }">
              <span>{{ match.match_status_txt }}</span>
              <img v-if="match.status === 2" src="@assets/icons/score/minite.gif" loading="lazy" />
            </div>
            <!-- 主队：黄牌数 + 队名 + 比分 -->
            <div class="team-single team-home">
              <span v-if="match?.corner?.[2] > 0" class="yellow-card-badge">
                {{ match.corner[2] }}
              </span>
              <span class="team-name">{{ match.home_team_name }}</span>
              <span class="score" :class="{ 'score-active': match.status !== 1 }">
                {{ statusMatchScoreText(match.status, match?.home_grades) }}
              </span>
              <span class="score-separator">-</span>
            </div>
            <!-- 客队：比分 + 队名 + 黄牌数 -->
            <div class="team-single team-away">
              <span class="score" :class="{ 'score-active': match.status !== 1 }">
                {{ statusMatchScoreText(match.status, match?.away_grades) }}
              </span>
              <span class="team-name">{{ match.away_team_name }}</span>
              <span v-if="match?.corner?.[3] > 0" class="yellow-card-badge">
                {{ match.corner[3] }}
              </span>
            </div>
            <div class="half-score">
              <div>{{ statusMatchScoreText(match.status, match?.half_score[0]) }}-{{ statusMatchScoreText(match.status, match?.half_score[1]) }}</div>
            </div>
            <div class="corner">
              <div>{{ statusMatchScoreText(match.status, match?.corner[0]) }}-{{ statusMatchScoreText(match.status, match?.corner[1]) }}</div>
            </div>
            <div class="betting-info-standard">
              <div class="index-row">
                <span v-for="(item, index) in match.w_1" :key="`w1-${index}`">{{ item }}</span>
              </div>
              <div class="odds-row">
                <span
                  v-for="(oddsItem, index) in match.w_2"
                  :key="`w2-${index}`"
                  :class="{
                    up: match.odds1_change === 'up',
                    down: match.odds1_change === 'down'
                  }"
                >
                  {{ oddsItem }}
                </span>
              </div>
            </div>
            <div class="icons">
              <img
                v-if="hasAnimationIcon(match)"
                src="@assets/icons/score/animation-active.webp"
                loading="lazy"
              />
              <img
                v-if="hasVideoIcon(match)"
                src="@assets/icons/score/video-active.webp"
                loading="lazy"
              />
            </div>
            <div class="favorite">
              <span class="data-link">分析</span>
              <span class="data-link">指数</span>
            </div>
            <div class="favorite">
              <img src="@assets/icons/score/collect.webp" alt="收藏" loading="lazy" />
            </div>
          </div>
        </template>
      </div>
    </div>
    <Legend />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import dayjs from 'dayjs'
import Legend from './Legend.vue'
import OddsFilterDropdown from './OddsFilterDropdown.vue'
import { scoreRightStore } from '@/stores/useScoreRight'

const useScoreRightStore = scoreRightStore()

const props = defineProps({
  list: Array,
  hasReset: Boolean,
  onSelectMatch: Function,
  selectedMatch: Object,
  // 新增：指数筛选类型
  oddsFilter: {
    type: Array,
    default: () => ['asian', 'overUnder', 'european']
  }
})

// 指数下拉菜单显示状态
const showOddsDropdown = ref(false)

// 选中的指数类型
const selectedOddsTypes = ref([...props.oddsFilter])

// 延迟关闭的定时器
let closeTimer = null

/**
 * 处理鼠标离开表头
 */
const handleMouseLeave = () => {
  // 延迟关闭，给用户时间移动到下拉框
  closeTimer = setTimeout(() => {
    showOddsDropdown.value = false
  }, 200)
}

/**
 * 处理鼠标进入（取消延迟关闭）
 */
const handleMouseEnter = () => {
  if (closeTimer) {
    clearTimeout(closeTimer)
    closeTimer = null
  }
  showOddsDropdown.value = true
}

/**
 * 处理指数筛选变化
 */
const handleOddsFilterChange = (selectedTypes) => {
  selectedOddsTypes.value = selectedTypes
  console.log('指数筛选已更新:', selectedTypes)
  // 这里不需要emit到父组件，因为筛选逻辑会在组件内部通过计算属性完成
}

/**
 * 根据选中的指数类型过滤指数数据
 * @param {Array} oddsArray - 原始指数数组 (w_1 或 w_2)
 * @returns {Array} 过滤后的指数数组
 * 
 * 数据结构假设：
 * - 索引 0-2: 亚盘数据（左、中、右）
 * - 索引 3-5: 大小球数据（左、中、右）
 * - 索引 6-8: 欧赔数据（左、中、右）
 */
const filterOddsData = (oddsArray) => {
  if (!oddsArray || !Array.isArray(oddsArray)) {
    return []
  }
  
  // 如果数据长度不是 9，直接返回原数据（兼容其他格式）
  if (oddsArray.length !== 9) {
    return oddsArray
  }
  
  const filtered = []
  
  // 按照用户选择的顺序添加数据
  selectedOddsTypes.value.forEach(type => {
    switch (type) {
      case 'asian': // 亚盘
        filtered.push(...oddsArray.slice(0, 3))
        break
      case 'overUnder': // 大小球
        filtered.push(...oddsArray.slice(3, 6))
        break
      case 'european': // 欧赔
        filtered.push(...oddsArray.slice(6, 9))
        break
    }
  })
  
  return filtered
}

/**
 * 点击其他区域关闭下拉菜单（保留以防万一）
 */
const handleClickOutside = (event) => {
  const target = event.target
  if (!target.closest('.odds-filter-trigger') && !target.closest('.odds-filter-dropdown')) {
    showOddsDropdown.value = false
  }
}

// 预加载状态图标
const preloadStatusIcons = () => {
  const icons = [
    'minite.gif',
    'animation-active.webp',
    'video-active.webp',
    'collect.webp'
  ]
  icons.forEach((icon) => {
    const img = new Image()
    img.src = new URL(`@assets/icons/score/${icon}`, import.meta.url).href
  })
}

// 分组比赛数据
const groupedMatches = computed(() => {
  return groupMatchesByStatus(props.list)
})

const getGroupTitle = (status) => {
  switch (status) {
    case 1:
      return '未开始'
    case 2:
      return '进行中'
    case 3:
      return '已结束'
    default:
      return ''
  }
}

const isSelected = (match) => {
  return match?.match_id === props.selectedMatch?.match_id
}

const selectMatch = (match) => {
  props.onSelectMatch(match)
  useScoreRightStore.getScoreItemData(match)
}

const formatTime = (num) => {
  return dayjs(num).format('HH:mm')
}

const statusMatchScoreText = (statusIndex, score) => {
  switch (statusIndex) {
    case 1:
      return '-'
    case 2:
    case 3:
      return score
    default:
      return '-'
  }
}

const hasAnimationIcon = (match) => {
  return match.has_vid !== 1 && match.has_anim === 1
}

const hasVideoIcon = (match) => {
  return match.has_vid === 1
}

// 根据热度描述获取样式类名
const getHotClass = (hotDesc) => {
  switch (hotDesc) {
    case '热门':
      return 'hot-level-1'
    case '重点':
      return 'hot-level-2'
    case '次重点':
      return 'hot-level-3'
    case '普通':
      return 'hot-level-4'
    case '一般':
      return 'hot-level-5'
    default:
      return ''
  }
}

// 分组函数
const groupMatchesByStatus = (matches = []) => {
  const groups = {
    inProgress: { status: 2, matches: [] },
    notStarted: { status: 1, matches: [] },
    finished: { status: 3, matches: [] }
  }

  if (!matches) return Object.values(groups)

  matches.forEach((match) => {
    if (match.status === 2) {
      groups.inProgress.matches.push(match)
    } else if (match.status === 1) {
      groups.notStarted.matches.push(match)
    } else if (match.status === 3) {
      groups.finished.matches.push(match)
    }
  })

  return Object.values(groups).filter((group) => group.matches.length > 0)
}

onMounted(() => {
  preloadStatusIcons()
  // 添加全局点击监听，用于关闭下拉菜单
  document.addEventListener('click', handleClickOutside)
})

// 组件卸载时清理事件监听
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped lang="scss">
.match-list-standard {
  width: 100%;
  border-radius: 10px;
  background-color: $scoreFootball-matchList-white;
  padding-bottom: 10px;
  margin-bottom: 30px;
  overflow: visible;
}

.header {
  display: flex;
  height: 45px;
  font-weight: 400;
  font-size: 13px;
  color: $scoreFootball-matchList-text-primary;
  align-items: center;
  background-color: $scoreFootball-matchList-white;
  border-bottom: 1px solid $scoreFootball-matchList-border;
}

.header-item {
  text-align: center;
  font-weight: 400;
  font-size: 13px;
  color: $scoreFootball-matchList-text-primary;
}

.header-item.header-time-league {
  flex: 1.2;
}

.header-item.header-progress {
  flex: 0.6;
}

.header-item.header-status {
  flex: 0.6;
}

.header-item.teams-single {
  flex: 1.5;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 4px;
  position: relative;
  
  // 隐藏原始文字
  font-size: 0;
  line-height: 0;
  overflow: hidden;
  
  // 主队表头：右对齐布局，与下方队伍名称对齐
  &.teams-single-home {
    justify-content: flex-end;
    
    // 创建一个与队名区域等宽的容器，内部居中显示表头文字
    &::before {
      content: '';
      flex: 1;
      min-width: 0;
    }
    
    // 表头文字容器，与队名区域对齐（max-width: 120px）
    &::after {
      content: '主队';
      max-width: 120px;
      width: 120px;
      text-align: center;
      font-weight: 400;
      font-size: 13px;
      color: $scoreFootball-matchList-text-primary;
      line-height: normal;
    }
  }
  
  // 客队表头：左对齐布局，与下方队伍名称对齐
  &.teams-single-away {
    justify-content: flex-start;
    
    // 表头文字容器，与队名区域对齐（max-width: 120px）
    &::before {
      content: '客队';
      max-width: 120px;
      width: 120px;
      text-align: center;
      font-weight: 400;
      font-size: 13px;
      color: $scoreFootball-matchList-text-primary;
      line-height: normal;
      margin-left: -8px; // 往左侧移动一点
    }
    
    &::after {
      content: '';
      flex: 1;
      min-width: 0;
    }
  }
}

.header-item.half-score,
.header-item.corner {
  flex: 0.5;
  font-size: 13px;
}

.header-item.betting-info {
  flex: 2.2;
  
  // 指数筛选触发器样式
  &.odds-filter-trigger {
    position: relative;
    cursor: pointer;
    user-select: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    transition: color 0.2s ease;
    
    &:hover {
      color: $scoreFootball-success;
      
      .filter-icon {
        stroke: $scoreFootball-success;
      }
    }
    
    .filter-icon {
      width: 14px;
      height: 14px;
      stroke: $scoreFootball-matchList-text-primary;
      transition: stroke 0.2s ease;
    }
  }
}

.header-item.icons {
  flex: 0.5;
}

.header-item.favorite {
  flex: 0.6;
}

.list-container {
  overflow-x: visible;
  position: relative;
}

.list {
  position: relative;
  width: 100%;
}

.match-item {
  display: flex;
  align-items: center;
  border-bottom: 1px solid $scoreFootball-matchList-border;
  position: relative;
  min-height: 40px;
  font-size: 11px;
  background: $scoreFootball-matchList-white;
  cursor: pointer;

  &:nth-child(2n) {
    background: $scoreFootball-matchList-bg-alternate;
  }

  &:hover {
    border: 1px solid $scoreFootball-matchList-active-border;
  }

  &.active {
    background: $scoreFootball-matchList-active-bg;
    border: 1px solid $scoreFootball-matchList-active-border;

    &::after {
      content: '';
      width: 9px;
      height: 9px;
      border-top: 1px solid #77abff;
      border-right: 1px solid #77abff;
      position: absolute;
      right: -5px;
      top: 42%;
      transform: rotate(45deg);
      background-color: #e7effc;
    }
  }
}

.time-league {
  flex: 1.2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 5px;
    width: 3px;
    height: 28px;
    background: $scoreFootball-matchList-progress-bar;
    border-radius: 2px;
    top: 50%;
    margin-top: -14px;
  }
}

// 热度等级样式
.hot-level-1 {
  &::after {
    background: $color-primary;
  }
}

.hot-level-2 {
  &::after {
    background: #ff8800;
  }
}

.hot-level-3 {
  &::after {
    background: #ffcc00;
  }
}

.hot-level-4 {
  &::after {
    background: #00cc88;
  }
}

.hot-level-5 {
  &::after {
    background: #888888;
  }
}

.hot {
  &::after {
    background: $scoreFootball-matchList-active-border;
  }
}

.league-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-left: 8px;
}

.time {
  flex: 0.6;
  color: $scoreFootball-matchList-text-secondary;
  text-align: center;
  font-size: 11px;
}

.league {
  color: $scoreFootball-matchList-text-secondary;
  width: 70px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  text-align: center;
  font-size: 11px;
}

.progress {
  flex: 0.6;
  color: $scoreFootball-matchList-text-light;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;

  img {
    width: 2px;
    margin-left: 2px;
  }
}

.progress-red {
  color: $scoreFootball-matchList-text-danger;
}

// 单行队伍显示样式
.team-single {
  flex: 1.5;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 4px;
  font-size: 11px;
}

.team-home {
  justify-content: flex-end;
  
  .team-name {
    max-width: 120px;
    text-align: right;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: $scoreFootball-matchList-text-dark;
  }
  
  .score {
    font-weight: bold;
    font-size: 13px;
    color: $scoreFootball-matchList-text-secondary;
    min-width: 15px;
    text-align: center;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    
    &.score-active {
      color: $scoreFootball-matchList-text-danger;
    }
  }
  
  .score-separator {
    font-size: 13px;
    color: $scoreFootball-matchList-text-secondary;
    margin: 0 4px;
    line-height: 1;
    display: inline-flex;
    align-items: center;
  }
}

.team-away {
  justify-content: flex-start;
  
  .score {
    font-weight: bold;
    font-size: 13px;
    color: $scoreFootball-matchList-text-secondary;
    min-width: 15px;
    text-align: center;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    
    &.score-active {
      color: $scoreFootball-matchList-text-danger;
    }
  }
  
  .team-name {
    max-width: 120px;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: $scoreFootball-matchList-text-dark;
  }
}

// 黄牌徽章样式（黄色方块）
.yellow-card-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  background: $yellow-card;
  border-radius: 3px;
  color: $scoreFootball-matchList-white;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  box-shadow: 0 1px 3px rgba(255, 191, 96, 0.3);
}

.half-score,
.corner {
  flex: 0.5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: $scoreFootball-matchList-text-secondary;
}

.betting-info-standard {
  flex: 2.2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 3px;
  padding: 4px 0;
}

.index-row,
.odds-row {
  display: flex;
  justify-content: space-around;
  width: 100%;
  font-size: 11px;
  
  span {
    flex: 1;
    text-align: center;
    color: $scoreFootball-matchList-text-secondary;
  }
}

.odds-row {
  font-weight: 500;
  
  span {
    color: $scoreFootball-matchList-text-dark;
    
    &.up {
      color: $scoreFootball-matchList-text-danger;
    }
    
    &.down {
      color: $scoreFootball-success;
    }
  }
}

.icons {
  flex: 0.5;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 2px;
}

.icons img {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.favorite {
  flex: 0.6;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.favorite img {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.data-link {
  font-size: 11px;
  color: $scoreFootball-success;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.7;
  }
}

.group-header {
  padding: 12px 15px;
  background-color: $scoreFootball-matchList-white;
  text-align: center;
  border-bottom: 1px solid $scoreFootball-matchList-border;
  color: $scoreFootball-matchList-text-dark;
  font-size: 11px;
  font-weight: 500;
}
</style>
